import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminListArticles, adminDeleteArticle } from '../../lib/api';
import { Loader2, FileText, Trash2, Edit, Plus, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

export default function AdminArticles() {
  const [articles, setArticles] = useState(null);
  const { toast } = useToast();

  const load = () => adminListArticles().then(setArticles);
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ? Cette action est irréversible.')) return;
    await adminDeleteArticle(id);
    toast({ title: 'Article supprimé' });
    load();
  };

  if (!articles) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  return (
    <div className="p-8 lg:p-10">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-sky-950">Articles</h1>
          <p className="text-slate-500 mt-1">{articles.length} article{articles.length > 1 ? 's' : ''} — {articles.filter((a) => a.published).length} publié{articles.filter((a) => a.published).length > 1 ? 's' : ''}</p>
        </div>
        <Link to="/admin/articles/new">
          <Button className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl px-5">
            <Plus className="w-4 h-4 mr-2" />Nouvel article
          </Button>
        </Link>
      </header>

      {articles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sky-100 p-16 text-center">
          <FileText className="w-12 h-12 text-sky-200 mx-auto mb-3" />
          <p className="text-slate-500 mb-5">Aucun article pour le moment.</p>
          <Link to="/admin/articles/new"><Button>Créer le premier</Button></Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {articles.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-sky-100 p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-sky-50 flex-shrink-0">
                {a.image && <img src={a.image} alt={a.title} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">{a.category}</span>
                  {a.published ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1"><Eye className="w-3 h-3" />Publié</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1"><EyeOff className="w-3 h-3" />Brouillon</span>
                  )}
                </div>
                <h3 className="font-display font-bold text-sky-950 truncate">{a.title}</h3>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{a.excerpt}</p>
                <p className="text-xs text-slate-400 mt-1">/{a.slug} • {a.date}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {a.published && (
                  <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-sky-50 text-slate-600" title="Voir">
                    <Eye className="w-4 h-4" />
                  </a>
                )}
                <Link to={`/admin/articles/${a.id}`} className="p-2 rounded-lg hover:bg-sky-50 text-sky-700">
                  <Edit className="w-4 h-4" />
                </Link>
                <button onClick={() => handleDelete(a.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
